import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box, Drawer, AppBar, Toolbar, List, ListItem, ListItemButton,
  ListItemIcon, ListItemText, Typography, IconButton, Avatar,
  Menu, MenuItem, Divider, Tooltip, useTheme,
} from '@mui/material'
import {
  Dashboard, DirectionsBus, Route, LocationOn, Assessment,
  Logout, Menu as MenuIcon, ChevronLeft, Person,
} from '@mui/icons-material'
import { useAuthStore } from '@/stores/authStore'

const DRAWER_WIDTH = 240
const DRAWER_MINI = 64

const NAV_ITEMS = [
  { label: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
  { label: 'Vehicles', icon: <DirectionsBus />, path: '/vehicles' },
  { label: 'Trips', icon: <Route />, path: '/trips' },
  { label: 'Live Tracking', icon: <LocationOn />, path: '/tracking' },
  { label: 'Reports', icon: <Assessment />, path: '/reports' },
]

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, clearAuth } = useAuthStore()
  const [expanded, setExpanded] = useState(true)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const drawerWidth = expanded ? DRAWER_WIDTH : DRAWER_MINI

  const handleLogout = () => {
    clearAuth()
    navigate('/login', { replace: true })
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            background: '#0D47A1',
            color: '#fff',
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        {/* Logo */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent={expanded ? 'space-between' : 'center'}
          px={expanded ? 2 : 0}
          py={1.5}
          sx={{ minHeight: 64 }}
        >
          {expanded && (
            <Box display="flex" alignItems="center" gap={1}>
              <DirectionsBus sx={{ color: '#FFB300' }} />
              <Typography variant="subtitle1" fontWeight={800} color="#fff" noWrap>
                BHEL GPS
              </Typography>
            </Box>
          )}
          <IconButton onClick={() => setExpanded(!expanded)} sx={{ color: '#fff' }}>
            {expanded ? <ChevronLeft /> : <MenuIcon />}
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />

        <List sx={{ mt: 1 }}>
          {NAV_ITEMS.map((item) => {
            const active = location.pathname === item.path
            return (
              <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
                <Tooltip title={expanded ? '' : item.label} placement="right">
                  <ListItemButton
                    onClick={() => navigate(item.path)}
                    sx={{
                      minHeight: 48,
                      justifyContent: expanded ? 'initial' : 'center',
                      px: 2.5,
                      borderRadius: 1,
                      mx: 1,
                      mb: 0.5,
                      bgcolor: active ? 'rgba(255,255,255,0.15)' : 'transparent',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.10)' },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: expanded ? 2 : 'auto',
                        justifyContent: 'center',
                        color: active ? '#FFB300' : 'rgba(255,255,255,0.7)',
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {expanded && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: 14,
                          fontWeight: active ? 700 : 400,
                          color: active ? '#fff' : 'rgba(255,255,255,0.8)',
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            )
          })}
        </List>

        {/* User at bottom */}
        <Box sx={{ mt: 'auto', p: 1 }}>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mb: 1 }} />
          <Tooltip title={expanded ? '' : user?.full_name || 'User'} placement="right">
            <ListItemButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.10)' },
                justifyContent: expanded ? 'initial' : 'center',
              }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#FFB300', mr: expanded ? 1.5 : 0 }}>
                <Person sx={{ fontSize: 18 }} />
              </Avatar>
              {expanded && (
                <Box overflow="hidden">
                  <Typography variant="body2" fontWeight={600} color="#fff" noWrap>
                    {user?.full_name || 'User'}
                  </Typography>
                  <Typography variant="caption" color="rgba(255,255,255,0.6)" noWrap>
                    {user?.role}
                  </Typography>
                </Box>
              )}
            </ListItemButton>
          </Tooltip>
        </Box>
      </Drawer>

      {/* User Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem disabled>
          <Typography variant="body2">{user?.email}</Typography>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <Logout fontSize="small" sx={{ mr: 1 }} /> Logout
        </MenuItem>
      </Menu>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: '#F5F7FA',
          minHeight: '100vh',
          overflow: 'auto',
          transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* Top Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: '#fff',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Toolbar>
            <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ flexGrow: 1 }}>
              {NAV_ITEMS.find((n) => n.path === location.pathname)?.label || 'BHEL GPS Fleet'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date().toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
            </Typography>
          </Toolbar>
        </AppBar>
        <Box p={3}>{children}</Box>
      </Box>
    </Box>
  )
}
